import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

const REDIRECT_BASE = 'https://crmpro1775042.builtwithrocket.new';

const GOOGLE_ADS_REDIRECT_URI = 'https://dashboard.wilcart.com/auth/callback';

const GOOGLE_OAUTH_URL = `https://accounts.google.com/o/oauth2/auth?client_id=${import.meta.env?.VITE_GOOGLE_ADS_CLIENT_ID}&redirect_uri=${encodeURIComponent(GOOGLE_ADS_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent('openid email profile https://www.googleapis.com/auth/adwords')}&access_type=offline&prompt=consent&state=google_ads`;

const META_OAUTH_URL = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${import.meta.env?.VITE_META_APP_ID}&redirect_uri=${encodeURIComponent(REDIRECT_BASE + '/settings?tab=integrations&oauth=meta')}&scope=ads_management,ads_read,business_management&response_type=code`;

const CredentialSection = ({ title, icon, iconBg, iconColor, isConnected, children, onDisconnect, disconnecting }) => (
  <div className={`border rounded-xl overflow-hidden transition-all ${isConnected ? 'border-green-200' : 'border-border'}`}>
    {/* Header */}
    <div className={`flex items-center justify-between px-5 py-4 ${isConnected ? 'bg-green-50/60' : 'bg-muted/30'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <h3 className="font-semibold text-foreground text-sm">{title}</h3>
          <div className={`inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
            isConnected ? 'bg-green-50 text-green-700 border-green-200' : 'bg-muted text-muted-foreground border-border'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'}`} />
            {isConnected ? 'Connected' : 'Not connected'}
          </div>
        </div>
      </div>
      {isConnected && (
        <button
          onClick={onDisconnect}
          disabled={disconnecting}
          className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 border border-red-200 bg-red-50 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
        >
          {disconnecting ? <div className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" /> : <Icon name="Unlink" size={12} />}
          Disconnect
        </button>
      )}
    </div>
    {/* Body */}
    <div className="p-5">{children}</div>
  </div>
);

const AdIntegrationsTab = ({ oauthProvider, oauthCode, onOAuthHandled }) => {
  const { organization, refreshOrganization } = useAuth();

  // Meta state
  const [metaToken, setMetaToken] = useState(organization?.meta_access_token ? '••••••••••••••••' : '');
  const [metaAdAccountId, setMetaAdAccountId] = useState(organization?.meta_ad_account_id || '');
  const [metaTokenEditing, setMetaTokenEditing] = useState(!organization?.meta_access_token);
  const [savingMeta, setSavingMeta] = useState(false);
  const [disconnectingMeta, setDisconnectingMeta] = useState(false);

  // Google state
  const [googleCustomerId, setGoogleCustomerId] = useState(organization?.google_ads_customer_id || '');
  const [googleRefreshToken, setGoogleRefreshToken] = useState(organization?.google_ads_refresh_token ? '••••••••••••••••' : '');
  const [googleTokenEditing, setGoogleTokenEditing] = useState(!organization?.google_ads_refresh_token);
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);

  // OAuth exchange state
  const [exchangingOAuth, setExchangingOAuth] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null); // 'success' | 'error' | null

  const isMetaConnected = !!organization?.meta_access_token;
  const isGoogleConnected = !!organization?.google_ads_refresh_token;

  // ── Handle OAuth code exchange ─────────────────────────────────
  useEffect(() => {
    if (!oauthProvider || !oauthCode || !organization?.id) return;

    // Google Ads: code was already exchanged in auth-callback, just refresh org and show status
    if (oauthProvider === 'google_status') {
      if (oauthCode === 'success') {
        refreshOrganization?.()?.then(() => {
          setGoogleRefreshToken('••••••••••••••••');
          setGoogleTokenEditing(false);
          setOauthStatus('google_success');
          toast?.success('Google Ads connected! Enter your Customer ID to complete setup.');
        });
      } else {
        const msg = new URLSearchParams(window.location.search)?.get('msg') || 'Google Ads connection failed';
        setOauthStatus('error');
        toast?.error(decodeURIComponent(msg));
      }
      onOAuthHandled?.();
      return;
    }

    const exchangeCode = async () => {
      setExchangingOAuth(true);
      setOauthStatus(null);

      try {
        if (oauthProvider === 'meta') {
          // Exchange Meta code for access token
          const res = await fetch(
            `https://graph.facebook.com/v18.0/oauth/access_token?client_id=${import.meta.env?.VITE_META_APP_ID}&redirect_uri=${encodeURIComponent(REDIRECT_BASE + '/settings?tab=integrations&oauth=meta')}&client_secret=${import.meta.env?.VITE_META_APP_SECRET}&code=${oauthCode}`
          );
          const data = await res?.json();

          if (data?.error) {
            throw new Error(data?.error?.message || 'Meta OAuth failed');
          }

          if (data?.access_token) {
            // Get long-lived token
            const llRes = await fetch(
              `https://graph.facebook.com/v18.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${import.meta.env?.VITE_META_APP_ID}&client_secret=${import.meta.env?.VITE_META_APP_SECRET}&fb_exchange_token=${data?.access_token}`
            );
            const llData = await llRes?.json();
            const finalToken = llData?.access_token || data?.access_token;

            // Fetch ad accounts
            const adAccountsRes = await fetch(
              `https://graph.facebook.com/v18.0/me/adaccounts?fields=id,name,account_status&access_token=${finalToken}`
            );
            const adAccountsData = await adAccountsRes?.json();
            const firstAccount = adAccountsData?.data?.[0];

            const { error } = await supabase?.from('organizations')?.update({
              meta_access_token: finalToken,
              meta_ad_account_id: firstAccount?.id?.replace('act_', '') || '',
            })?.eq('id', organization?.id);

            if (error) throw error;
            await refreshOrganization?.();
            setMetaToken('••••••••••••••••');
            setMetaTokenEditing(false);
            if (firstAccount?.id) {
              setMetaAdAccountId(firstAccount?.id?.replace('act_', ''));
            }
            setOauthStatus('meta_success');
            toast?.success('Facebook Ads connected successfully!');
          }
        } else if (oauthProvider === 'google') {
          // Exchange Google code for tokens
          const tokenRes = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
              code: oauthCode,
              client_id: import.meta.env?.VITE_GOOGLE_ADS_CLIENT_ID || '',
              client_secret: import.meta.env?.VITE_GOOGLE_ADS_CLIENT_SECRET || '',
              redirect_uri: GOOGLE_ADS_REDIRECT_URI,
              grant_type: 'authorization_code',
            }),
          });
          const tokenData = await tokenRes?.json();

          if (tokenData?.error) {
            throw new Error(tokenData?.error_description || 'Google OAuth failed');
          }

          if (tokenData?.refresh_token) {
            const { error } = await supabase?.from('organizations')?.update({
              google_ads_refresh_token: tokenData?.refresh_token,
            })?.eq('id', organization?.id);

            if (error) throw error;
            await refreshOrganization?.();
            setGoogleRefreshToken('••••••••••••••••');
            setGoogleTokenEditing(false);
            setOauthStatus('google_success');
            toast?.success('Google Ads connected! Enter your Customer ID to complete setup.');
          } else {
            throw new Error('No refresh token received. Make sure you selected "Allow" and have offline access.');
          }
        }
      } catch (err) {
        console.error('OAuth exchange error:', err);
        setOauthStatus('error');
        toast?.error(err?.message || 'OAuth connection failed');
      } finally {
        setExchangingOAuth(false);
        onOAuthHandled?.();
      }
    };

    exchangeCode();
  }, [oauthProvider, oauthCode, organization?.id]);

  // ── Meta handlers ──────────────────────────────────────────────
  const handleSaveMeta = async () => {
    if (!metaAdAccountId?.trim()) { toast?.error('Ad Account ID is required'); return; }
    const tokenToSave = metaTokenEditing ? metaToken?.trim() : organization?.meta_access_token;
    if (!tokenToSave) { toast?.error('Access Token is required'); return; }
    setSavingMeta(true);
    try {
      const updates = {
        meta_access_token: tokenToSave,
        meta_ad_account_id: metaAdAccountId?.trim(),
      };
      const { error } = await supabase?.from('organizations')?.update(updates)?.eq('id', organization?.id);
      if (error) throw error;
      await refreshOrganization?.();
      setMetaTokenEditing(false);
      setMetaToken('••••••••••••••••');
      toast?.success('Meta credentials saved');
    } catch (err) {
      toast?.error('Failed to save Meta credentials');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDisconnectMeta = async () => {
    setDisconnectingMeta(true);
    try {
      const { error } = await supabase?.from('organizations')?.update({ meta_access_token: null, meta_ad_account_id: null })?.eq('id', organization?.id);
      if (error) throw error;
      await refreshOrganization?.();
      setMetaToken('');
      setMetaAdAccountId('');
      setMetaTokenEditing(true);
      toast?.success('Meta disconnected');
    } catch (err) {
      toast?.error('Failed to disconnect Meta');
    } finally {
      setDisconnectingMeta(false);
    }
  };

  // ── Google handlers ────────────────────────────────────────────
  const handleSaveGoogle = async () => {
    if (!googleCustomerId?.trim()) { toast?.error('Customer ID is required'); return; }
    const tokenToSave = googleTokenEditing ? googleRefreshToken?.trim() : organization?.google_ads_refresh_token;
    if (!tokenToSave) { toast?.error('Refresh Token is required'); return; }
    setSavingGoogle(true);
    try {
      const updates = {
        google_ads_customer_id: googleCustomerId?.trim()?.replace(/-/g, ''),
        google_ads_refresh_token: tokenToSave,
      };
      const { error } = await supabase?.from('organizations')?.update(updates)?.eq('id', organization?.id);
      if (error) throw error;
      await refreshOrganization?.();
      setGoogleTokenEditing(false);
      setGoogleRefreshToken('••••••••••••••••');
      toast?.success('Google Ads credentials saved');
    } catch (err) {
      toast?.error('Failed to save Google Ads credentials');
    } finally {
      setSavingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setDisconnectingGoogle(true);
    try {
      const { error } = await supabase?.from('organizations')?.update({ google_ads_customer_id: null, google_ads_refresh_token: null })?.eq('id', organization?.id);
      if (error) throw error;
      await refreshOrganization?.();
      setGoogleCustomerId('');
      setGoogleRefreshToken('');
      setGoogleTokenEditing(true);
      toast?.success('Google Ads disconnected');
    } catch (err) {
      toast?.error('Failed to disconnect Google Ads');
    } finally {
      setDisconnectingGoogle(false);
    }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      {/* Section header */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-1">AI Marketing Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Connect your ad accounts to launch campaigns directly from the AI Marketing module. Credentials are stored encrypted per organization.
        </p>
      </div>
      {/* OAuth processing banner */}
      {exchangingOAuth && (
        <div className="flex items-center gap-3 p-4 bg-violet-50 border border-violet-200 rounded-xl">
          <div className="w-5 h-5 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-violet-800">Connecting your account...</p>
            <p className="text-xs text-violet-600">Exchanging authorization code for access tokens</p>
          </div>
        </div>
      )}
      {/* OAuth success banners */}
      {oauthStatus === 'meta_success' && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <Icon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Facebook Ads connected!</p>
            <p className="text-xs text-green-700">Your first ad account was auto-selected. You can change the Ad Account ID below if needed.</p>
          </div>
          <button onClick={() => setOauthStatus(null)} className="ml-auto text-green-600 hover:text-green-800">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {oauthStatus === 'google_success' && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <Icon name="CheckCircle" size={18} className="text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Google Ads authorized!</p>
            <p className="text-xs text-green-700">Enter your Google Ads Customer ID below to complete the setup.</p>
          </div>
          <button onClick={() => setOauthStatus(null)} className="ml-auto text-green-600 hover:text-green-800">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {oauthStatus === 'error' && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <Icon name="AlertCircle" size={18} className="text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">OAuth connection failed</p>
            <p className="text-xs text-red-700">Please try again or enter credentials manually below.</p>
          </div>
          <button onClick={() => setOauthStatus(null)} className="ml-auto text-red-600 hover:text-red-800">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {/* ── Meta / Facebook ─────────────────────────────────────── */}
      <CredentialSection
        title="Facebook / Meta Ads"
        icon="Facebook"
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        isConnected={isMetaConnected}
        onDisconnect={handleDisconnectMeta}
        disconnecting={disconnectingMeta}
      >
        {/* OAuth button */}
        <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="Zap" size={15} className="text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-blue-800">Connect with OAuth</p>
              <p className="text-xs text-blue-600 truncate">One-click authorization — no tokens needed</p>
            </div>
          </div>
          <button
            onClick={() => { window.location.href = META_OAUTH_URL; }}
            disabled={exchangingOAuth}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors flex-shrink-0 whitespace-nowrap disabled:opacity-50"
          >
            <Icon name="Facebook" size={13} />
            {isMetaConnected ? 'Reconnect' : 'Connect Facebook'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground font-medium">or enter manually</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Instructions */}
        <div className="p-3 bg-muted/40 border border-border rounded-lg text-xs text-muted-foreground mb-4">
          <p className="font-medium text-foreground mb-1">How to get your Meta credentials:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Go to <strong>Meta Business Manager → Settings → System Users</strong></li>
            <li>Create a System User with Ad Account access</li>
            <li>Generate a token with <code className="bg-muted px-1 rounded">ads_management</code> permission</li>
            <li>Your Ad Account ID is in <strong>Business Settings → Ad Accounts</strong></li>
          </ol>
        </div>

        {/* Fields */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Meta Access Token <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="password"
                value={metaToken}
                onChange={(e) => setMetaToken(e?.target?.value)}
                onFocus={() => { if (!metaTokenEditing) { setMetaToken(''); setMetaTokenEditing(true); } }}
                placeholder="EAAxxxxxxxxxxxxxxx..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 pr-20"
              />
              {isMetaConnected && !metaTokenEditing && (
                <button
                  onClick={() => { setMetaToken(''); setMetaTokenEditing(true); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary hover:underline"
                >
                  Change
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Ad Account ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={metaAdAccountId}
              onChange={(e) => setMetaAdAccountId(e?.target?.value)}
              placeholder="123456789"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Numeric ID only — without the "act_" prefix</p>
          </div>
        </div>

        <button
          onClick={handleSaveMeta}
          disabled={savingMeta}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {savingMeta ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Save" size={14} />}
          {isMetaConnected ? 'Update Credentials' : 'Save & Connect'}
        </button>
      </CredentialSection>
      {/* ── Google Ads ───────────────────────────────────────────── */}
      <CredentialSection
        title="Google Ads"
        icon="Search"
        iconBg="bg-orange-50"
        iconColor="text-orange-600"
        isConnected={isGoogleConnected}
        onDisconnect={handleDisconnectGoogle}
        disconnecting={disconnectingGoogle}
      >
        {/* OAuth button */}
        <div className="mb-5 p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="Zap" size={15} className="text-orange-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-orange-800">Connect with OAuth</p>
              <p className="text-xs text-orange-600 truncate">One-click authorization — no tokens needed</p>
            </div>
          </div>
          <button
            onClick={async () => {
              await supabase?.auth?.signInWithOAuth({
                provider: 'google',
                options: {
                  scopes: 'openid email profile https://www.googleapis.com/auth/adwords',
                  queryParams: { access_type: 'offline', prompt: 'consent' },
                  redirectTo: `${GOOGLE_ADS_REDIRECT_URI}?state=google_ads`,
                },
              });
            }}
            disabled={exchangingOAuth}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg text-xs font-semibold hover:bg-orange-600 transition-colors flex-shrink-0 whitespace-nowrap disabled:opacity-50"
          >
            <Icon name="Search" size={13} />
            {isGoogleConnected ? 'Reconnect' : 'Connect Google Ads'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground font-medium">or enter manually</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Instructions */}
        <div className="p-3 bg-muted/40 border border-border rounded-lg text-xs text-muted-foreground mb-4">
          <p className="font-medium text-foreground mb-1">How to get your Google Ads credentials:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Go to <strong>Google Cloud Console → APIs → Google Ads API</strong></li>
            <li>Create OAuth 2.0 credentials (Web Application)</li>
            <li>Use the <strong>OAuth 2.0 Playground</strong> to exchange for a refresh token</li>
            <li>Your Customer ID is in <strong>Google Ads → Account Settings</strong> (format: 123-456-7890)</li>
          </ol>
        </div>

        {/* Fields */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Customer ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={googleCustomerId}
              onChange={(e) => setGoogleCustomerId(e?.target?.value)}
              placeholder="123-456-7890"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Dashes are stripped automatically on save</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Refresh Token <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="password"
                value={googleRefreshToken}
                onChange={(e) => setGoogleRefreshToken(e?.target?.value)}
                onFocus={() => { if (!googleTokenEditing) { setGoogleRefreshToken(''); setGoogleTokenEditing(true); } }}
                placeholder="1//xxxxxxxxxxxxxxx..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 pr-20"
              />
              {isGoogleConnected && !googleTokenEditing && (
                <button
                  onClick={() => { setGoogleRefreshToken(''); setGoogleTokenEditing(true); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-primary hover:underline"
                >
                  Change
                </button>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveGoogle}
          disabled={savingGoogle}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50"
        >
          {savingGoogle ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Save" size={14} />}
          {isGoogleConnected ? 'Update Credentials' : 'Save & Connect'}
        </button>
      </CredentialSection>
      {/* Info note */}
      <div className="flex items-start gap-3 p-4 bg-muted/40 border border-border rounded-xl">
        <Icon name="ShieldCheck" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          All credentials are stored encrypted in your organization record and are never exposed in the browser. They are used exclusively when launching campaigns via the AI Marketing module.
        </p>
      </div>
    </div>
  );
};

export default AdIntegrationsTab;
